<template>
  <div class="tag-relation-list">
    <template v-for="unitGroup in tagOfGroupList">
      <div
        class="tag-relation-group"
        :key="unitGroup.id"
        v-if="
          unitGroup.isCaseGroup
            ? unitGroup.offsetTags.length > 0 || unitGroup.emptyTagFields.length > 0
            : unitGroup.relationArr.length > 0 || unitGroup.invalidEntityArr.length > 0
        "
      >
        <div class="tag-relation-group-title single-ellipsis">
          <span>{{ unitGroup.name }}</span>
          <el-popover
            v-if="unitGroup.emptyTagFields.length > 0"
            ref="addEmptyEntityRefs"
            placement="right"
            trigger="hover"
            class="tag-relation-add-wrap"
            :style="
              addWrapVisibleUnitGroupId === unitGroup.id || (unitGroup.isCaseGroup && unitGroup.offsetTags.length < 1)
                ? { visibility: 'visible' }
                : {}
            "
            @show="addWrapVisibleUnitGroupId = unitGroup.id"
            @hide="updAddWrapPopoverVisible = ''"
          >
            <el-button slot="reference" type="text" class="tag-relation-add-btn">
              <i class="el-icon-plus"></i>
              <span>添加</span>
            </el-button>
            <div class="bottom-field-content-field-picker-ul">
              <div
                v-for="fieldDetail in unitGroup.emptyTagFields"
                :key="fieldDetail._id"
                class="bottom-field-content-field-picker-item"
                @click="nodeItem => addFieldEntityHandle(nodeItem, unitGroup.emptyTagFields, fieldDetail)"
              >
                <span>{{ fieldDetail.title }}</span>
              </div>
            </div>
          </el-popover>
        </div>

        <div class="tag-offset-ul" v-if="unitGroup.isCaseGroup">
          <bottomFieldInfoRender
            v-for="(tagItem, tagIdx) in unitGroup.offsetTags"
            :key="`${tagItem.dataInfo.id}_${tagIdx}`"
            :entityDetail="tagItem"
            :showTools="!resetDelShow"
            @clear="() => emit('delTag', tagItem)"
            @changeFieldVal="val => emit('changeFieldVal', { val, entityDetail: tagItem })"
          />
        </div>

        <template v-else>
          <div v-for="relationTreeItem in unitGroup.relationArr" :key="relationTreeItem.fullId">
            <div class="tag-offset-ul">
              <bottomFieldInfoRender
                v-for="(tagItem, tagIdx) in relationTreeItem.parentOffsetTags"
                :key="`${tagItem.dataInfo.id}_${tagIdx}`"
                :entityDetail="tagItem"
                :showTools="!resetDelShow"
                showFieldTitle
                :couldUpdRelation="tagItem.fieldDetail.groupRelationId !== 'case'"
                @clear="() => emit('delTag', tagItem)"
                @changeFieldVal="val => emit('changeFieldVal', { val, entityDetail: tagItem })"
                @updRelation="nodePosition => updRelationHandle(nodePosition, tagItem)"
              />
              <template v-if="relationTreeItem.relationOffsetTags.length > 0">
                <bottomFieldInfoRender
                  v-for="(tagItem, tagIdx) in relationTreeItem.relationOffsetTags"
                  :key="`${tagItem.dataInfo.id}_${tagIdx}`"
                  :entityDetail="tagItem"
                  :showTools="!resetDelShow"
                  :couldUpdRelation="unitGroup.relationArr.length > 0"
                  @clear="() => emit('delTag', tagItem)"
                  @changeFieldVal="val => emit('changeFieldVal', { val, entityDetail: tagItem })"
                  @updRelation="nodePosition => updRelationHandle(nodePosition, tagItem)"
                />
              </template>
            </div>
          </div>

          <div class="tag-offset-ul" v-if="(unitGroup.invalidEntityArr || []).length > 0">
            <bottomFieldInfoRender
              v-for="(tagItem, tagIdx) in unitGroup.invalidEntityArr"
              :key="`${tagItem.dataInfo.id}_${tagIdx}`"
              :entityDetail="tagItem"
              :showTools="!resetDelShow"
              isInvalid
              :couldUpdRelation="unitGroup.relationArr.length > 0"
              @clear="() => emit('delTag', tagItem)"
              @changeFieldVal="val => emit('changeFieldVal', { val, entityDetail: tagItem })"
              @updRelation="nodePosition => updRelationHandle(nodePosition, tagItem)"
            />
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue';
import { couldGroupFiled } from '@/lib/utils';
import dayjs from 'dayjs';
import { groupBy, flatten } from 'lodash';

import bottomFieldInfoRender from '../bottomFieldInfoRender';

const emit = defineEmits(['changeFieldVal', 'tapAction', 'delTag']);
const props = defineProps({
  row: {
    type: Object,
    required: true
  },
  resetDelShow: {
    type: Boolean,
    default: false
  }
});

const addWrapVisibleUnitGroupId = ref('');

const courseUnitGroups = inject('courseUnitGroups', () => []);
const fieldsGroupByGroupRelationId = inject('fieldsGroupByGroupRelationId', () => []);
const fieldsGroupByFieldName = inject('fieldsGroupByFieldName', () => []);
const relationGroupByFieldEntityId = computed(() => groupBy(props.row.relation || [], 'entityId'));
const groupingCaseGroupByRelationId = computed(() => groupBy(props.row.groupingCase || [], 'groupRelationId'));
const allGroupingCaseGroupFieldEntityIds = computed(() => {
  return [...new Set(flatten((props.row.groupingCase || []).map(o => o.fullId.split('/'))))];
});
const groupMainFieldNames = computed(() => {
  const fieldNames = [];

  courseUnitGroups.value.forEach(unitGroup => {
    const { nerIds = [], labelIds = [] } = unitGroup;

    fieldNames.push(...nerIds, ...labelIds);
  });

  return [...new Set(fieldNames)];
});

const tagOfGroupList = computed(() => {
  return courseUnitGroups.value.map(unitGroup => {
    const offsetTags = [];
    let relationArr = [];
    const invalidEntityArr = [];
    const emptyTagFields = [];

    const relationFields = fieldsGroupByGroupRelationId.value[unitGroup.id] || [];

    if (unitGroup.id === 'case') {
      relationFields
        .filter(fieldDetail => {
          return !groupMainFieldNames.value.includes(fieldDetail.field_name);
        })
        .forEach(fieldDetail => {
          const fieldTagDataArr = (props.row._source[fieldDetail.field_name] || {}).data || [];
          if (fieldTagDataArr.length < 1) {
            emptyTagFields.push(fieldDetail);
          } else {
            fieldTagDataArr.forEach(dataInfo => {
              const { text, tagContent, fieldVal } = fetchTagTextAndContent(fieldDetail, dataInfo);
              offsetTags.push({
                dataInfo,
                fieldDetail,
                fieldVal,
                text,
                tagContent
              });
            });
          }
        });

      return { ...unitGroup, isCaseGroup: true, offsetTags, emptyTagFields };
    }

    relationFields.forEach(fieldDetail => {
      const fieldTagDataArr = (props.row._source[fieldDetail.field_name] || {}).data || [];
      if (fieldTagDataArr.length < 1) {
        !groupMainFieldNames.value.includes(fieldDetail.field_name) && emptyTagFields.push(fieldDetail);
      } else {
        fieldTagDataArr.forEach(dataInfo => {
          if (allGroupingCaseGroupFieldEntityIds.value.includes(dataInfo.id)) {
            return;
          }
          const { text, tagContent, fieldVal } = fetchTagTextAndContent(fieldDetail, dataInfo);
          const curEntityRelation = relationGroupByFieldEntityId.value[dataInfo.id] || [];
          const relationFullIds = curEntityRelation.map(relationItem => relationItem.fullId);
          invalidEntityArr.push({
            dataInfo,
            fieldDetail,
            fieldVal,
            text,
            tagContent,
            relation: curEntityRelation,
            relationFullIds
          });
        });
      }
    });

    const relationEntityArr = [];

    const { nerIds = [], labelIds = [] } = unitGroup;
    const parentEntityEnum = {};
    [...nerIds, ...labelIds].forEach(fieldName => {
      ((props.row._source[fieldName] || {}).data || []).forEach(dataInfo => {
        parentEntityEnum[dataInfo.id] = dataInfo;
      });
    });

    relationArr = (groupingCaseGroupByRelationId.value[unitGroup.id] || []).map(groupingCaseItem => {
      const parentOffsetTags = [];
      const relationOffsetTags = [];

      groupingCaseItem.fullId.split('/').forEach(entityId => {
        const parentEntityDataInfo = parentEntityEnum[entityId];
        if (!parentEntityDataInfo) {
          return;
        }
        const fieldDetail = fieldsGroupByFieldName.value[parentEntityDataInfo.labelId];
        if (!fieldDetail) {
          return;
        }
        const { text, tagContent, fieldVal } = fetchTagTextAndContent(fieldDetail, parentEntityDataInfo);
        parentOffsetTags.push({
          dataInfo: parentEntityDataInfo,
          relation: relationGroupByFieldEntityId.value[parentEntityDataInfo.id] || [],
          fieldDetail,
          fieldVal,
          text,
          tagContent
        });
      });

      const curRelationEntityArr = invalidEntityArr.filter(tagItem => {
        return tagItem.relationFullIds.includes(groupingCaseItem.fullId);
      });
      if (curRelationEntityArr.length > 0) {
        relationEntityArr.push(...curRelationEntityArr);
        relationOffsetTags.push(...curRelationEntityArr);
      }

      return { ...groupingCaseItem, parentOffsetTags, relationOffsetTags: relationOffsetTags };
    });

    return {
      ...unitGroup,
      isCaseGroup: false,
      relationArr,
      invalidEntityArr: invalidEntityArr.filter(o => !relationEntityArr.includes(o)),
      emptyTagFields
    };
  });
});

const fetchTagTextAndContent = (fieldDetail, dataInfo) => {
  if (!fieldDetail) {
    return { text: '', tagContent: '', fieldVal: '' };
  }

  let text = fieldDetail.title;
  let tagContent = '';
  let fieldVal = '';

  if (fieldDetail.type === 'text') {
    if (fieldDetail.deputy_type === 'dict') {
      text = dataInfo.label;
    } else if (couldGroupFiled(fieldDetail)) {
      text = dataInfo.content;
    } else {
      tagContent = dataInfo.content;
    }
  }

  if (fieldDetail.type === 'date' && 'timestamp' in dataInfo) {
    fieldVal = dayjs(dataInfo.timestamp);
  }

  if (fieldDetail.type === 'num' && 'value' in dataInfo) {
    fieldVal = dataInfo.value;
  }

  if (fieldVal !== '') {
    text = `${text} - ${formatFieldValText(fieldDetail, fieldVal)}`;
  }

  return { text, tagContent, fieldVal };
};

const formatFieldValText = (fieldDetail, fieldVal) => {
  if (fieldDetail.type === 'date') {
    return dayjs(fieldVal).format('YYYY-MM-DD');
  } else {
    return fieldVal;
  }
};

const addEmptyEntityRefs = ref(null);
const addFieldEntityHandle = (nodeItem, fields, fieldDetail) => {
  const nodePosition = nodeItem.target.getBoundingClientRect();
  emit('tapAction', {
    action: 'addEmptyEntity',
    info: {
      nodePosition,
      fieldDetail,
      fields
    }
  });
  addEmptyEntityRefs.value.forEach(el => el.doClose());
};

const updRelationHandle = (nodePosition, tagItem) => {
  emit('tapAction', {
    action: 'updRelation',
    info: {
      nodePosition,
      fieldDetail: tagItem.fieldDetail,
      entityDetail: tagItem
    }
  });
};
</script>

<style lang="scss">
.bottom-field-content-field-picker-ul {
  box-sizing: border-box;
  padding: 12px 6px;
  width: 180px;
  max-height: 260px;
  overflow: auto;
  .bottom-field-content-field-picker-item {
    box-sizing: border-box;
    font-size: 14px;
    color: #2c3443;
    line-height: 20px;
    cursor: pointer;
    padding: 4px 6px;
    word-break: break-all;

    &:hover {
      background-color: #eaf6ff;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '../../index';

.tag-relation-list {
  .tag-relation-group {
    padding: 10px 20px 0;
    margin: 10px 0;
    border-top: 1px solid #e8ebef;

    .tag-relation-group-title {
      position: relative;
      box-sizing: border-box;
      color: #9599a5;
      font-size: 14px;
      line-height: 20px;
      width: fit-content;
      max-width: 100%;
      padding-right: 50px;

      .tag-relation-add-wrap {
        position: absolute;
        top: 50%;
        right: 0px;
        transform: translateY(-50%);
        visibility: hidden;

        .tag-relation-add-btn {
          padding: 0;

          .el-icon-plus {
            font-size: 14px;
          }

          span {
            margin-left: 2px;
            vertical-align: text-top;
          }
        }
      }
    }
    &:hover {
      .tag-relation-add-wrap {
        visibility: visible;
      }
    }

    ::v-deep .tag-offset-ul {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      font-size: 14px;
      width: calc(100% + 10px);
      margin-left: -10px;

      .field-item {
        position: relative;
        max-width: 100%;
        display: flex;
        align-items: center;
        margin: 4px 0 4px 10px;

        .field-item-title {
          flex-shrink: 0;
          color: #2c3443;
          line-height: 24px;
          max-width: 200px;
        }

        .field-item-content {
          box-sizing: border-box;
          padding: 4px 6px;
          border-radius: 2px;

          .upd-popover-popover {
            position: absolute;
            right: 14px;
            top: 0;
          }

          .tool-btn {
            position: absolute;
            right: 0;
            top: 0;
            width: 28px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }

          &.have-tool {
            padding-right: 34px;
          }

          &.add-btn {
            color: rgba(#3d465a, 0.8);
            border: 1px solid #d2d8e3;
            cursor: pointer;
            width: 86px;
            text-align: center;

            .iconfont {
              font-size: 14px;
              margin-right: 2px;
            }
          }
        }
      }
    }
  }
}
</style>
